import { Injectable } from "@angular/core";
import { catchError, switchMap } from "rxjs/operators";
import { Observable, of, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { APIResponse } from "../../../models/Response";
import { Meeting, MeetingOrganizerResponse, RecMeeting, RecurringChildMeetingsList } from "../models/meeting.model";
import { Constants } from "../../../models/constants";
import { UserDetailsService } from "../../../services/UserAccountRealted/user-details.service";

@Injectable({
    providedIn: "root",
})
export class CreateMeetingService {
    apiServerUrl = environment.apiBaseUrl;

    constructor(
        private http: HttpClient,
        private userDetailsService: UserDetailsService,
    ) {}

    createMeeting(meetingDetails: Meeting, accountId: number): Observable<APIResponse<Meeting>> {
        return this.http
            .post<APIResponse<Meeting>>(`${this.apiServerUrl}/meeting/createMeeting`, meetingDetails, {
                headers: {
                    accountIds: accountId.toString(),
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    updateMeeting(meetingDetails: Meeting, accountId: number): Observable<APIResponse<Meeting>> {
        return this.http
            .post<APIResponse<Meeting>>(
                `${this.apiServerUrl}/meeting/updateMeeting/${meetingDetails.meetingId}`,
                meetingDetails,
                {
                    headers: {
                        accountIds: accountId.toString(),
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    createRecMeeting(meetingDetails: RecMeeting, userAccountId: string): Observable<APIResponse<RecMeeting>> {
        const newHeaders = {
            accountIds: userAccountId,
        };
        return this.http
            .post<APIResponse<RecMeeting>>(`${this.apiServerUrl}/meeting/createRecurringMeeting`, meetingDetails, {
                headers: newHeaders,
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    getRecMeetingPerPage(
        pageNumber: number,
        pageSize: number,
        recurringMeetingId: number,
    ): Observable<APIResponse<RecurringChildMeetingsList>> {
        return this.http
            .get<APIResponse<RecurringChildMeetingsList>>(
                `${this.apiServerUrl}/meeting/getRecurringMeeting/${recurringMeetingId}/${pageNumber}/${pageSize}`,
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    getMeetingByMeetingId(meetingId: number, headerAccountIds: string): Observable<APIResponse<Meeting>> {
        const headers = {
            accountIds: headerAccountIds,
        };
        return this.http
            .get<APIResponse<Meeting>>(`${this.apiServerUrl}/meeting/getMeeting/${meetingId}`, { headers })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    getMeetingByMeetingNumber(request: any, headerAccountIds: string): Observable<APIResponse<Meeting>> {
        const headers = {
            accountIds: headerAccountIds,
        };
        return this.http
            .post<APIResponse<Meeting>>(`${this.apiServerUrl}/meeting/v2/getMeeting`, request, { headers })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    getRecMeetingByRecurringMeetingId(
        recurringMeetingId: number,
        headerAccountIds: string,
    ): Observable<APIResponse<RecMeeting>> {
        const headers = {
            accountIds: headerAccountIds,
        };
        return this.http
            .get<APIResponse<RecMeeting>>(`${this.apiServerUrl}/meeting/getRecurringMeeting/${recurringMeetingId}`, {
                headers,
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    updateRecMeeting(meetingDetails: RecMeeting, headerAccountIds: string): Observable<APIResponse<RecMeeting>> {
        const headers = {
            accountIds: headerAccountIds,
        };
        return this.http
            .post<APIResponse<RecMeeting>>(
                `${this.apiServerUrl}/meeting/updateRecurringMeeting/${meetingDetails.recurringMeetingId}`,
                meetingDetails,
                { headers },
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    updateMeetingOrganizerResponse(
        meeting: MeetingOrganizerResponse,
        headers: string,
    ): Observable<APIResponse<MeetingOrganizerResponse>> {
        let newHttpHeaders = new HttpHeaders();
        newHttpHeaders.append("accountIds", headers);
        return this.http
            .post<APIResponse<MeetingOrganizerResponse>>(
                `${this.apiServerUrl}/meeting/updateOrganizerResponse`,
                meeting,
                { headers: newHttpHeaders },
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    openMeetingView(meetingId: number, headersAccountIds: string): Observable<any> {
        return this.getMeetingByMeetingId(meetingId, headersAccountIds).pipe(
            switchMap((meetingResponse) => {
                const meeting = meetingResponse?.data;
                if (!meeting) {
                    return throwError(() => new Error("Meeting not found"));
                }
                const entityTypeId = meeting.teamId
                    ? Constants.EntityTypes.TEAM
                    : meeting.projectId
                    ? Constants.EntityTypes.PROJECT
                    : Constants.EntityTypes.ORG;
                const entityId = meeting.teamId || meeting.projectId || meeting.orgId;
                return this.userDetailsService.getEntityMembers(entityTypeId, entityId, headersAccountIds).pipe(
                    switchMap((teamMembersResponse) => {
                        if (!teamMembersResponse?.data) {
                            return throwError(() => new Error("Team members not found"));
                        }
                        return of({
                            meeting: meetingResponse.data,
                            teamMembers: teamMembersResponse.data,
                        });
                    }),
                );
            }),
            catchError((error) => throwError(() => error)),
        );
    }
}
